/* eslint-disable max-lines */
import {
  AfterViewInit,
  Component,
  computed,
  ContentChild,
  HostListener,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import {
  FlightShopModule,
  FlightShopWidgetTemplates
} from '@com-delta-idp/mnx-capability-flight-shop-smart';
import { LoginComponent } from '@com-delta-idp/mnx-capability-login';
import { LoginModalWrapperModule } from '@com-delta-idp/mnx-login';
import { PrimaryNavActionEvent } from '@com-delta-idp/mnx-sitewide-header-ui';
import { LOGIN_AEM_MOCKS } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { PrimaryId } from '../../data-access/constants/navigation-links/navigation-linkts-constants';
import { AlternateFlow } from '../../data-access/models/interface/header/header.interfaces';
import { HeaderBaseComponent } from './header-base/header-base.component';
import { HEADER_IMPORTS } from './header-imports';

@Component({
  selector: 'idp-header',
  standalone: true,
  imports: [
    ...HEADER_IMPORTS,
    FlightShopModule,
    LoginComponent,
    LoginModalWrapperModule
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HeaderComponent
  extends HeaderBaseComponent
  implements OnInit, AfterViewInit
{
  isLightHeader = input(false);
  collapsedHeader = input(false);
  alternateFlow = input<AlternateFlow>({} as AlternateFlow);
  shopHeader = input(false);
  vacationHeader = input(false);
  startOverVacation = input(false);
  showAlertAdvisories = input(false);
  fromApp = input('siteWideHeader');
  centerPage = input(false);
  redirectUrl = input('');
  showFafHeader = input(false);
  fafBackLinkLabel = input('TRIP DETAILS');

  fafBackNavigationClicked = output<void>();

  startOverButtonClickedEvent = output();

  @ContentChild('vacationBookingWidget')
  vacationBookingWidget: TemplateRef<void> | null = null;

  // For local testing - use mock content if API fails
  loginMockContent = LOGIN_AEM_MOCKS;

  flightTemplate = FlightShopWidgetTemplates;
  isScreenUpTo768 = signal(window.innerWidth <= 768);
  isSlimHeader = computed(() => this.shopHeader() || this.vacationHeader());
  shouldHideSearchIcon = computed(
    () => this.isSlimHeader() && this.isScreenUpTo768()
  );

  private lastInnerWidth = -1;
  visibilityHidden = this.appConfig.get('APP') === 'site-search';

  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth === this.lastInnerWidth) return;
    this.lastInnerWidth = window.innerWidth;
    const previousScreenType = this.screenDetection.screenType();
    this.updateScreenType();
    this.isScreenUpTo768.set(window.innerWidth <= 768);
    this.headerState.showLogo = this.screenDetection.isDesktop();
    this.handleDesktopResize();
    this.handleScreenTypeChange(previousScreenType);
    this.handleSpecialHeaders();
  }

  private handleDesktopResize(): void {
    if (!this.screenDetection.isDesktop()) return;
    this.headerState.hamburgerMenuOpen = false;
    this.headerState.displayCloseIcon.set(false);
    this.renderer.setStyle(document.body, 'overflow', 'auto');
    this.headerState.resetExpandPanel(true);
  }

  private handleScreenTypeChange(previousScreenType: string): void {
    if (previousScreenType !== this.screenDetection.screenType()) {
      this.hideSecondaryDesktopNav();
    }
  }

  onFafBackNavigation(): void {
    this.fafBackNavigationClicked.emit();
  }

  private handleSpecialHeaders(): void {
    if (this.collapsedHeader()) this.initCollapsedHeader();
    if (this.shopHeader()) this.resetShopHeaderState();
  }

  private resetShopHeaderState(): void {
    this.clickFromMobileNavButton = false;
    this.headerState.selectedNav.set('flights');
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event) {
    const htmlElement = event.target as HTMLElement;
    this.closeSecondaryNavOnTouchOutside(htmlElement);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.headerState.displayCloseIcon() && event.key === 'Tab') {
      const navElement = this.elementRef.nativeElement.querySelector('nav');
      if (navElement) {
        this.domManipulation.trapFocus(event, navElement);
      }
    }
  }

  ngOnInit(): void {
    this.onWindowResize();
    this.checkForAccountType();
    this.checkDisableLegacy();
    this.checkMachCoreLoginModal();
    this.checkLightHeaderFooter();
    this.flightsWidgetFacade.init();
    this.transSitesLangService.detectCurrentLanguage();
    this.subscribeToFlyoutLang();
    this.headerState.checkRestrictProfileSelector();
  }

  ngAfterViewInit(): void {
    if (this.headerState.displayCloseIcon()) {
      const navElement = this.elementRef.nativeElement.querySelector('nav');
      if (navElement) this.domManipulation.updateFocusableElements(navElement);
    }
    this.setMutationalObserver();
    this.initCollapsedHeader();
  }

  private initCollapsedHeader(): void {
    if (this.collapsedHeader()) {
      this.headerState.selectedNav.set('');
      this.headerState.previousPrimarySelectedNav = '';
      this.headerState.collapsedHeader = true;
    }
  }

  checkLightHeaderFooter(): void {
    if (this.isLightHeader()) this.headerState.isLightHeaderFooterEnable = true;
  }

  primaryNavAction(
    event: PrimaryNavActionEvent,
    clickFromMobileNavButton = false
  ): void {
    this.clickFromMobileNavButton = clickFromMobileNavButton;

    const isPrimaryNav = PrimaryId.includes(this.headerState.selectedNav());
    const newNavValue = this.getNavValue(event.id, this.collapsedHeader());
    this.headerState.selectedNav.set(newNavValue);
    this.selectedPrimaryNav = this.headerState.selectedNav();

    this.headerState.previousPrimarySelectedNav = this.getPreviousNav(
      event.id,
      newNavValue,
      isPrimaryNav
    );

    this.headerState.triggerNavClick();
    this.headerState.hamburgerMenuOpen = false;
    this.handleMobileNavAction();
  }

  private getPreviousNav(
    eventId: string,
    newNavValue: string,
    isPrimaryNav: boolean
  ): string {
    if (newNavValue === '') return '';
    return isPrimaryNav ? this.headerState.selectedNav() : eventId;
  }

  private handleMobileNavAction(): void {
    if (!this.screenDetection.isMobile()) return;
    const isTripRelated = this.headerState.isTripRelatedNav();
    this.headerState.mobileWidgetNavClick.set(isTripRelated);
    this.displayCloseIcon(this.shopHeader());
    if (!isTripRelated) this.closeIconClicked();

    this.expandMobileWidgetFromCollapseView();
  }

  expandMobileWidgetFromCollapseView(): void {
    if (this.clickFromMobileNavButton) {
      this.mobileViewShowOptions();
      this.headerState.uiStateManagementService.setExpandView(true);
    }
  }

  mobileViewShowOptions(): void {
    this.headerState.mobileWidgetNavClick.set(true);
    this.displayCloseIcon(this.shopHeader());
    this.bodyScrollService.disableScroll();
    this.cdr.detectChanges();
  }
}
